import { FunctionComponent, ReactNode, useCallback, useMemo } from 'react'

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import styled, { css } from 'styled-components'

import useHasInvertedColors from '@hooks/useHasInvertedColors'
import usePageMetadata from '@hooks/usePageMetadata'
import useSubscribeToSportsEventCache from '@hooks/useSubscribeToSportsEventCache'
import { SportTableInlineProps } from '@widgets/SportTableInline/types'
import SponsorLink from '@components/SportTableInline/SponsorLink'

import HeaderCell from './HeaderCell'
import NumberCell from './NumberCell'
import OutcomeCell from './OutcomeCell'
import StandingsLegend from './StandingsLegend'
import TeamCell from './TeamCell'

import type {
  DataRow,
  OutcomeType,
  Table as TableType,
  ValidAPIResponse,
} from '@hooks/useSportsStandings'

type TableProps = Pick<ValidAPIResponse, 'header' | 'outcomeTypeColors'> &
  Pick<TableType, 'title' | 'dataRows' | 'legend'> &
  Pick<SportTableInlineProps, 'sponsor' | 'typeOfSport'>

interface TableHeaderProps {
  columnType: string
}

type StyledTableHeaderProps = Pick<TableHeaderProps, 'columnType'>
type InvertedColorsStyledProps = {
  hasInvertedColors: boolean
}
type StyledTableCellProps = Pick<TableHeaderProps, 'columnType'>

type StyledTableRowProps = InvertedColorsStyledProps & {
  isHighlighted?: boolean
}

const StyledTableWrapper = styled.div``

const StyledTable = styled.table`
  width: 100%;
  height: 100%;
  border-collapse: collapse;
`

const StyledTableHeaderRow = styled.tr<InvertedColorsStyledProps>`
  ${({
    theme: {
      colors: { staticNeutral300, strokeWeak },
    },
    hasInvertedColors,
  }) => css`
    border-bottom: 1px solid
      ${hasInvertedColors ? staticNeutral300 : strokeWeak};
  `}
`

const StyledTableRow = styled.tr<StyledTableRowProps>`
  ${({
    theme: {
      colors: { staticNeutral300, strokeWeak, fillWeak, staticNeutral950 },
    },
    hasInvertedColors,
    isHighlighted,
  }) => css`
    border-bottom: 1px solid
      ${hasInvertedColors ? staticNeutral300 : strokeWeak};
    ${isHighlighted &&
    css`
      background-color: ${hasInvertedColors ? staticNeutral950 : fillWeak};
    `};
  `}
`

const StyledTableCell = styled.td<StyledTableCellProps>`
  padding: 0;
  height: 100%;
  ${({ columnType }) => {
    return css`
      padding: 0;
      width: ${columnType === 'team'
        ? 'auto'
        : columnType === 'outcomeType'
          ? '4px'
          : columnType === 'points'
            ? '52px'
            : '40px'};
    `
  }}
`

const StyledTableHeader = styled.th<StyledTableHeaderProps>`
  ${({ columnType }) => {
    return css`
      padding: 0;
      width: ${columnType === 'team'
        ? 'auto'
        : columnType === 'outcomeType'
          ? '4px'
          : columnType === 'points'
            ? '52px'
            : '40px'};
    `
  }}
`

const StyledTableBody = styled.tbody``

const StyledTableHead = styled.thead``

const StyledTableTitle = styled.div<InvertedColorsStyledProps>`
  ${({
    theme: {
      colors: { textDefault, staticNeutral100 },
      typography: {
        headings: {
          small: { bundledCSS: smallHeadingCSS },
        },
      },
      utils: { ellipsis },
    },
    hasInvertedColors,
  }) => css`
    ${smallHeadingCSS};
    color: ${hasInvertedColors ? staticNeutral100 : textDefault};
    ${ellipsis('100%', 2)};
  `}
`

const StyledHeaderWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    margin-bottom: ${spacing12};
  `}
`

const columnHelper = createColumnHelper<DataRow>()

const Table: FunctionComponent<TableProps> = ({
  title,
  dataRows,
  header,
  legend,
  outcomeTypeColors,
  sponsor,
  typeOfSport,
}) => {
  const hasInvertedColors = useHasInvertedColors()
  const pageMetadata = usePageMetadata()
  const { matchId, sportsData } = pageMetadata
  const existingSportsEventData = useSubscribeToSportsEventCache({
    matchId: matchId ?? '',
    typeOfSport,
  })

  const isHighlighted = useCallback(
    (id: string) => {
      const teams = existingSportsEventData?.formattedData.teams

      return (
        sportsData?.sportradarId === id ||
        teams?.some((team) => team.sportradarId === id)
      )
    },
    [existingSportsEventData, sportsData]
  )

  const columns = useMemo(
    () =>
      header.map((headerItem) =>
        columnHelper.accessor(headerItem.id, {
          header: () => (
            <HeaderCell
              columnType={headerItem.id}
              hasInvertedColors={hasInvertedColors}>
              {headerItem.name}
            </HeaderCell>
          ),
          ...(headerItem.id === 'team'
            ? {
                cell: ({ getValue }: any) => (
                  <TeamCell
                    teamId={getValue()?.sportradarId}
                    teamLogoSrc={getValue()?.logoSrc}
                    teamName={getValue()?.name}
                    hasInvertedColors={hasInvertedColors}
                  />
                ),
              }
            : headerItem.id === 'outcomeType'
              ? {
                  cell: ({ getValue }) => {
                    const value = getValue() as OutcomeType

                    return (
                      <OutcomeCell
                        title={
                          value
                            ? legend.find((item) => !!item[value])?.[value]
                            : ''
                        }
                        backgroundColor={
                          value ? outcomeTypeColors[value] : 'transparent'
                        }
                      />
                    )
                  },
                }
              : {
                  cell: ({ getValue, row }) => {
                    const outcomeType = row.original?.outcomeType
                    const value = getValue()

                    return (
                      <NumberCell
                        title={
                          outcomeType
                            ? legend.find((item) => !!item[outcomeType])?.[
                                outcomeType
                              ]
                            : ''
                        }
                        columnType={headerItem.id}
                        hasInvertedColors={hasInvertedColors}>
                        <>{value as ReactNode}</>
                      </NumberCell>
                    )
                  },
                }),
        })
      ),
    [header, outcomeTypeColors, legend, hasInvertedColors]
  )

  const data = useMemo<DataRow[]>(
    () =>
      dataRows.map((row) => ({
        ...row,
        outcomeType: row?.outcomeType,
      })),
    [dataRows]
  )

  const tableInstance = useReactTable<DataRow>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  })

  const { getHeaderGroups, getRowModel } = tableInstance
  return (
    <StyledTableWrapper>
      <StyledHeaderWrapper>
        <StyledTableTitle hasInvertedColors={hasInvertedColors}>
          {title}
        </StyledTableTitle>
        {sponsor && (
          <SponsorLink
            sponsor={sponsor}
            hasInvertedColors={hasInvertedColors}
          />
        )}
      </StyledHeaderWrapper>
      <StyledTable>
        <StyledTableHead>
          {getHeaderGroups().map((headerGroup) => (
            <StyledTableHeaderRow
              key={headerGroup.id}
              hasInvertedColors={hasInvertedColors}>
              {headerGroup.headers.map((header) => (
                <StyledTableHeader key={header.id} columnType={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </StyledTableHeader>
              ))}
            </StyledTableHeaderRow>
          ))}
        </StyledTableHead>
        <StyledTableBody>
          {getRowModel().rows.map((row) => (
            <StyledTableRow
              key={row.id}
              hasInvertedColors={hasInvertedColors}
              isHighlighted={isHighlighted(row.original.team.sportradarId)}>
              {row.getVisibleCells().map((cell) => (
                <StyledTableCell key={cell.id} columnType={cell.column.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </StyledTableBody>
      </StyledTable>
      {legend.length > 0 && (
        <StandingsLegend
          legend={legend}
          outcomeTypeColors={outcomeTypeColors}
          hasInvertedColors={hasInvertedColors}
        />
      )}
    </StyledTableWrapper>
  )
}

export default Table
